/**
 * Insurance company detail.
 *
 * Added in the detail-page consistency pass: an insurer was one of the entities users
 * expect to be able to open, but it was reachable only as a card in a grid with an edit
 * dialog behind it — so "how is Interamerican performing, what have they settled, what
 * is open with them" had no home.
 *
 * No server work was required. Policies, claims and settlements all already filter by
 * `insuranceCompanyId`, and the dashboard summary accepts it too, so the metrics are the
 * same figures the dashboard computes, scoped to one carrier.
 *
 * Structure is the shared skeleton (components/common/detail-page.tsx), identical to the
 * client, policy and claim pages.
 */

import * as React from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ExternalLink, Mail, Pencil, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { VEHICLE_CATEGORIES, type InsuranceCompanyDto, type PolicyDto, type SettlementDto } from '@ias/shared';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton, Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/misc';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { EnumBadge } from '@/components/common/enum-badge';
import { EmptyState, Field, FieldGrid } from '@/components/common/page-header';
import {
  DetailHeader,
  DetailMetrics,
  DetailPage,
  DetailPageSkeleton,
  DetailTabsBar,
  Metric,
  Panel,
  Section,
  SectionStack,
  Workspace,
} from '@/components/common/detail-page';
import { EntityAvatar } from '@/components/common/entity-avatar';
import { DocumentPanel } from '@/components/common/document-panel';
import { PolicyRef } from '@/components/common/policy-ref';
import {
  claimsApi,
  policiesApi,
  settlementsApi,
  useDashboardSummary,
  useInsuranceCompanies,
  useSaveInsurerCommissionRates,
} from '@/lib/api/domains';
import { useAuth } from '@/lib/auth';
import { useI18n } from '@/lib/i18n';

import { DaysBadge, policyRowTint, policyStatusTone } from './policies/list';
import { InsurerFormDialog } from './insurers';

/** A carrier number is only useful if you can dial it from where you read it. */
function PhoneLink({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return (
    <a href={`tel:${value}`} className="text-primary inline-flex items-center gap-1.5 hover:underline">
      <Phone className="size-3.5 shrink-0" />
      {value}
    </a>
  );
}

export function InsurerDetailPage() {
  const { id = '' } = useParams();
  const { t, money, number, percent } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();

  // Reuses the list query the insurers grid already holds — same cache key, no new request.
  const { data: companies, isLoading } = useInsuranceCompanies(true);
  const company = companies?.find((candidate) => candidate.id === id);

  const { data: summary } = useDashboardSummary({ insuranceCompanyId: id });
  const [editOpen, setEditOpen] = React.useState(false);

  if (isLoading) return <DetailPageSkeleton />;
  if (!company) return <EmptyState title={t('error.notFound')} hint={t('error.notFoundHint')} />;

  return (
    <DetailPage>
      <DetailHeader
        breadcrumbs={[{ label: t('company.title'), to: '/insurers' }, { label: company.name }]}
        avatar={<EntityAvatar name={company.name} size="lg" />}
        title={company.name}
        status={
          company.isActive ? undefined : (
            <span className="text-muted-foreground border-border-strong rounded-md border px-2 py-0.5 text-xs font-medium">
              {t('common.no')}
            </span>
          )
        }
        meta={[
          ...(company.code ? [{ key: 'code', label: <span className="tabular">{company.code}</span> }] : []),
          {
            key: 'commission',
            label: `${t('company.defaultCommission')} ${percent(Number(company.defaultCommissionPercentage))}`,
          },
          {
            key: 'settlement',
            label: `${company.settlementDays ?? '—'} ${t('company.settlementDays').toLowerCase()}`,
          },
        ]}
        actions={
          <>
            <Button variant="outline" onClick={() => navigate(`/policies?insuranceCompanyId=${id}`)}>
              {t('policy.title')}
            </Button>
            {can('reference:write') && (
              <Button variant="ghost" onClick={() => setEditOpen(true)}>
                <Pencil /> {t('action.edit')}
              </Button>
            )}
          </>
        }
      />

      <DetailMetrics>
        <Metric
          label={t('dashboard.activePolicies')}
          value={summary ? number(summary.activePolicies) : '—'}
          isLoading={!summary}
        />
        <Metric
          label={t('policy.premium')}
          value={summary ? money(summary.totalPremium) : '—'}
          isLoading={!summary}
        />
        <Metric
          label={t('policy.commission')}
          value={summary ? money(summary.totalCommission) : '—'}
          isLoading={!summary}
        />
        <Metric
          label={t('dashboard.upcomingRenewals')}
          value={summary ? number(summary.upcomingRenewals) : '—'}
          isLoading={!summary}
        />
        <Metric
          label={t('dashboard.unpaidPolicies')}
          value={summary ? number(summary.unpaidPolicies) : '—'}
          tone={summary && summary.unpaidPolicies > 0 ? 'warning' : undefined}
          isLoading={!summary}
        />
        <Metric
          label={t('dashboard.openClaims')}
          value={summary ? number(summary.openClaims) : '—'}
          isLoading={!summary}
        />
      </DetailMetrics>

      <Tabs defaultValue="policies">
        <DetailTabsBar>
          <TabsList variant="underline">
            <TabsTrigger value="policies">{t('policy.title')}</TabsTrigger>
            <TabsTrigger value="claims">{t('claim.title')}</TabsTrigger>
            <TabsTrigger value="settlements">{t('commission.title')}</TabsTrigger>
            <TabsTrigger value="documents">{t('insurer.documents')}</TabsTrigger>
            <TabsTrigger value="info">{t('common.details')}</TabsTrigger>
          </TabsList>
        </DetailTabsBar>

        <TabsContent value="policies">
          <Workspace aside={<CarrierRail company={company} />}>
            <CompanyPolicies companyId={id} />
          </Workspace>
        </TabsContent>

        <TabsContent value="claims">
          <CompanyClaims companyId={id} />
        </TabsContent>

        <TabsContent value="settlements">
          <CompanySettlements companyId={id} />
        </TabsContent>

        {/* The shared panel, fourth scope. Upload, preview and delete-with-confirmation all
            come along unchanged — which is the whole return on parameterising it by scope. */}
        <TabsContent value="documents">
          <DocumentPanel scope="insurers" ownerId={id} typeEnum="insurerDocumentType" />
        </TabsContent>

        <TabsContent value="info">
          <SectionStack>
            <Section title={t('section.identity')}>
              <FieldGrid>
                <Field label={t('company.name')}>{company.name}</Field>
                <Field label={t('company.code')} mono>
                  {company.code ?? '—'}
                </Field>
              </FieldGrid>
            </Section>

            <Section title={t('section.terms')}>
              <FieldGrid>
                <Field label={t('company.defaultCommission')} mono>
                  {percent(Number(company.defaultCommissionPercentage))}
                </Field>
                <Field label={t('company.settlementDays')} mono>
                  {company.settlementDays ?? '—'}
                </Field>
              </FieldGrid>
            </Section>

            <CommissionRatesPanel company={company} />

            <Section title={t('section.contact')}>
              <FieldGrid>
                <Field label={t('client.phone')} mono>
                  {company.contactPhone ?? '—'}
                </Field>
                <Field label={t('client.email')}>{company.contactEmail ?? '—'}</Field>
              </FieldGrid>
            </Section>

            {/*
              A section of its own, with the hint spelled out, because the failure mode is
              handing a client the wrong number. The one above reaches the agency's account
              manager at the carrier; these reach the carrier's own desks, and they are what an
              agent reads out to somebody standing next to a damaged car. Rendered as `tel:`
              links so the agent can dial from the record instead of copying digits.
            */}
            <Section title={t('insurer.clientFacing')} description={t('insurer.clientFacingHint')}>
              <FieldGrid>
                <Field label={t('insurer.claimsPhone')} mono>
                  <PhoneLink value={company.claimsPhone} />
                </Field>
                <Field label={t('insurer.roadsidePhone')} mono>
                  <PhoneLink value={company.roadsidePhone} />
                </Field>
                <Field label={t('insurer.emergencyPhone')} mono>
                  <PhoneLink value={company.emergencyPhone} />
                </Field>
                <Field label={t('insurer.portalUrl')}>
                  {company.portalUrl ? (
                    <a
                      href={company.portalUrl}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-primary inline-flex items-center gap-1.5 hover:underline"
                    >
                      <ExternalLink className="size-3.5 shrink-0" />
                      <span className="truncate">{company.portalUrl.replace(/^https?:\/\//, '')}</span>
                    </a>
                  ) : (
                    '—'
                  )}
                </Field>
              </FieldGrid>
            </Section>

            {company.notes && (
              <Section title={t('section.notes')}>
                <p className="text-[13.5px] leading-relaxed whitespace-pre-line">{company.notes}</p>
              </Section>
            )}
          </SectionStack>
        </TabsContent>
      </Tabs>

      <InsurerFormDialog open={editOpen} onOpenChange={setEditOpen} company={company} />
    </DetailPage>
  );
}

/**
 * The carrier's commission grid — one rate per vehicle category.
 *
 * The screen this replaces was a single «Προεπιλεγμένη προμήθεια» field, which assumed a carrier
 * pays one rate. It does not: ΙΧ επιβατικό, ΕΙΧ ενοικιαζόμενο, ΦΙΧ and ΜΟΤΟ are priced separately
 * by every insurer the office works with, so one number could never pre-fill a policy form
 * correctly and the operator typed the rate in from memory every time.
 *
 * Every category is rendered, including the ones with no rate — a grid of blanks is what makes it
 * obvious which categories are still falling back to the default, and «ΜΟΤΟ is missing» is not a
 * question an add-a-row list can be scanned for. Blank means «use the default», so clearing a box
 * is how a category is removed.
 *
 * It saves the whole grid in one request, because it is one negotiation: «η Υδρόγειος πληρώνει 18
 * στα ΙΧ, 22 στα ΜΟΤΟ» is a single decision typed on a single screen.
 */
function CommissionRatesPanel({ company }: { company: InsuranceCompanyDto }) {
  const { t, percent } = useI18n();
  const { can } = useAuth();
  const save = useSaveInsurerCommissionRates();
  const editable = can('reference:write');

  const rates = React.useMemo(
    () =>
      Object.fromEntries(
        (company.commissionRates ?? []).map((rate) => [rate.vehicleCategory, rate.commissionPercentage]),
      ),
    [company.commissionRates],
  );

  const [draft, setDraft] = React.useState<Record<string, string>>(rates);
  // Re-seeded when the carrier's saved grid changes — after a save, and when this panel is
  // rendered for a different carrier without unmounting.
  React.useEffect(() => setDraft(rates), [rates]);

  const dirty = VEHICLE_CATEGORIES.some(
    (category) => (draft[category] ?? '') !== (rates[category] ?? ''),
  );

  const submit = async () => {
    await save.mutateAsync({
      companyId: company.id,
      rates: VEHICLE_CATEGORIES.filter((category) => (draft[category] ?? '').trim() !== '').map(
        (category) => ({
          vehicleCategory: category,
          commissionPercentage: draft[category]!.trim(),
        }),
      ),
    });
    toast.success(t('insurer.ratesSaved'));
  };

  return (
    <Section
      title={t('insurer.rates')}
      description={t('insurer.ratesHint')}
      actions={
        editable && (
          <Button size="sm" onClick={submit} disabled={!dirty} loading={save.isPending}>
            {t('action.save')}
          </Button>
        )
      }
    >
      <div className="grid gap-x-4 gap-y-2.5 sm:grid-cols-2">
        {VEHICLE_CATEGORIES.map((category) => (
          <div key={category} className="flex items-center justify-between gap-3">
            <EnumBadge enum="vehicleCategory" value={category} size="sm" />
            {editable ? (
              <div className="flex items-center gap-1.5">
                <Input
                  type="number"
                  inputMode="decimal"
                  step="0.01"
                  min="0"
                  max="100"
                  aria-label={t('policy.commissionPct')}
                  className="tabular w-24 text-right"
                  /* The default, shown as the placeholder: the box is genuinely empty, and what
                     it would fall back to is the one thing worth knowing before typing in it. */
                  placeholder={company.defaultCommissionPercentage ?? t('insurer.ratesFallback')}
                  value={draft[category] ?? ''}
                  onChange={(event) =>
                    setDraft((previous) => ({ ...previous, [category]: event.target.value }))
                  }
                />
                <span className="text-muted-soft text-xs">%</span>
              </div>
            ) : (
              <span className="tabular text-[13.5px]">
                {rates[category] ? (
                  percent(Number(rates[category]))
                ) : (
                  <span className="text-muted-soft">{t('insurer.ratesFallback')}</span>
                )}
              </span>
            )}
          </div>
        ))}
      </div>
    </Section>
  );
}

function CarrierRail({
  company,
}: {
  company: { contactPhone: string | null; contactEmail: string | null; notes: string | null };
}) {
  const { t } = useI18n();

  return (
    <SectionStack>
      <Section title={t('section.contact')}>
        <div className="space-y-2.5 text-[13.5px]">
          {company.contactPhone && (
            <a
              href={`tel:${company.contactPhone}`}
              className="hover:text-primary flex items-center gap-2.5 transition-colors duration-150"
            >
              <Phone className="text-muted-soft size-3.5 shrink-0" />
              <span className="tabular">{company.contactPhone}</span>
            </a>
          )}
          {company.contactEmail && (
            <a
              href={`mailto:${company.contactEmail}`}
              className="hover:text-primary flex items-center gap-2.5 transition-colors duration-150"
            >
              <Mail className="text-muted-soft size-3.5 shrink-0" />
              <span className="truncate">{company.contactEmail}</span>
            </a>
          )}
          {!company.contactPhone && !company.contactEmail && <p className="text-muted-soft">—</p>}
        </div>
      </Section>

      {company.notes && (
        <Section title={t('section.notes')}>
          <p className="text-muted-foreground text-[13px] leading-relaxed whitespace-pre-line">
            {company.notes}
          </p>
        </Section>
      )}
    </SectionStack>
  );
}

function CompanyPolicies({ companyId }: { companyId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = policiesApi.useList({
    insuranceCompanyId: companyId,
    perPage: 100,
    sortBy: 'endDate',
  });

  if (isLoading) return <Skeleton className="h-48 w-full" />;
  if (!data?.data.length) return <EmptyState title={t('common.empty')} hint={t('policy.title')} compact />;

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('policy.number')}</TableHead>
            <TableHead>{t('client.one')}</TableHead>
            <TableHead>{t('policy.endDate')}</TableHead>
            <TableHead className="text-right">{t('policy.premium')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.data.map((policy: PolicyDto) => (
            <TableRow key={policy.id} className={policyRowTint(policy)}>
              <TableCell className="py-2.5">
                <Link
                  to={`/policies/${policy.id}`}
                  className="text-primary tabular font-medium hover:underline"
                >
                  <PolicyRef policyNumber={policy.policyNumber} plates={policy.plates} muted />
                </Link>
              </TableCell>
              <TableCell className="py-2.5">
                <Link to={`/clients/${policy.clientId}`} className="hover:text-primary truncate">
                  {policy.clientName}
                </Link>
              </TableCell>
              <TableCell className="py-2.5">
                <span className="flex items-center gap-2">
                  <span className="tabular">{date(policy.endDate)}</span>
                  <DaysBadge days={policy.daysUntilExpiry} />
                </span>
              </TableCell>
              <TableCell className="tabular py-2.5 text-right">{money(policy.premiumAmount)}</TableCell>
              <TableCell className="py-2.5">
                <EnumBadge enum="policyStatus" value={policy.status} tone={policyStatusTone(policy)} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

function CompanyClaims({ companyId }: { companyId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = claimsApi.useList({ insuranceCompanyId: companyId, perPage: 100 });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.data.length) return <EmptyState title={t('common.empty')} hint={t('claim.title')} compact />;

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('claim.number')}</TableHead>
            <TableHead>{t('client.one')}</TableHead>
            <TableHead>{t('claim.incidentDate')}</TableHead>
            <TableHead className="text-right">{t('claim.amount')}</TableHead>
            <TableHead className="text-right">{t('claim.payout')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.data.map((claim) => (
            <TableRow key={claim.id}>
              <TableCell className="py-2.5">
                <Link to={`/claims/${claim.id}`} className="text-primary font-medium hover:underline">
                  {claim.claimNumber ?? claim.id.slice(0, 8)}
                </Link>
              </TableCell>
              <TableCell className="truncate py-2.5">{claim.clientName}</TableCell>
              <TableCell className="tabular py-2.5">{date(claim.incidentDate)}</TableCell>
              <TableCell className="tabular py-2.5 text-right">{money(claim.claimAmount)}</TableCell>
              <TableCell className="tabular py-2.5 text-right">{money(claim.payoutAmount)}</TableCell>
              <TableCell className="py-2.5">
                <EnumBadge enum="claimStatus" value={claim.status} size="sm" />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Panel>
  );
}

/** The variance column is the point — what the carrier says they paid vs. what is owed. */
function CompanySettlements({ companyId }: { companyId: string }) {
  const { t, money, date } = useI18n();
  const { data, isLoading } = settlementsApi.useList({ insuranceCompanyId: companyId, perPage: 100 });

  if (isLoading) return <Skeleton className="h-40 w-full" />;
  if (!data?.data.length)
    return <EmptyState title={t('common.empty')} hint={t('commission.title')} compact />;

  return (
    <Panel>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t('commission.reference')}</TableHead>
            <TableHead>{t('common.dateRange')}</TableHead>
            <TableHead className="text-right">{t('commission.declared')}</TableHead>
            <TableHead className="text-right">{t('commission.expected')}</TableHead>
            <TableHead className="text-right">{t('commission.variance')}</TableHead>
            <TableHead>{t('policy.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.data.map((settlement: SettlementDto) => {
            const variance = Number(settlement.varianceAmount);
            return (
              <TableRow key={settlement.id}>
                <TableCell className="tabular py-2.5 font-medium">
                  {settlement.statementReference}
                </TableCell>
                <TableCell className="tabular text-muted-foreground py-2.5 whitespace-nowrap">
                  {date(settlement.periodFrom)} – {date(settlement.periodTo)}
                </TableCell>
                <TableCell className="tabular py-2.5 text-right">
                  {money(settlement.declaredAmount)}
                </TableCell>
                <TableCell className="tabular text-muted-foreground py-2.5 text-right">
                  {money(settlement.expectedAmount)}
                </TableCell>
                <TableCell
                  className={
                    variance === 0
                      ? 'tabular text-muted-soft py-2.5 text-right'
                      : variance < 0
                        ? 'tabular text-critical py-2.5 text-right font-medium'
                        : 'tabular text-good py-2.5 text-right font-medium'
                  }
                >
                  {money(settlement.varianceAmount)}
                </TableCell>
                <TableCell className="py-2.5">
                  <EnumBadge enum="settlementStatus" value={settlement.status} size="sm" />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Panel>
  );
}
